import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Briefcase, Users, Star, AlertTriangle, TrendingUp, UserCheck, ChevronRight, FileText, Bell} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

interface Stats {
  vacantes: number
  postulantes: number
  colaboradores: number
  evaluacionesPendientes: number
}

const STAT_STYLES = [
  { color: 'text-blue-600', bg: 'bg-blue-50' },
  { color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { color: 'text-violet-600', bg: 'bg-violet-50' },
  { color: 'text-amber-600', bg: 'bg-amber-50' },
]

function EmpresaDashboard() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState<Stats>({ vacantes: 0, postulantes: 0, colaboradores: 0, evaluacionesPendientes: 0 })
  const [companyName, setCompanyName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    const fetchData = async () => {
      try {
        if (user) {
          const [compRes, vacRes, appRes, linkRes] = await Promise.all([
            lumi.entities.companies.list({ filter: { userId: user.userId }, limit: 1 }),
            lumi.entities.vacancies.list({ filter: { companyUserId: user.userId, status: 'publicada' }, limit: 1 }),
            lumi.entities.applications.list({ filter: { companyUserId: user.userId }, limit: 1 }),
            lumi.entities.links.list({ filter: { companyUserId: user.userId, status: 'aceptada' }, limit: 1 }),
          ])
          if (compRes.list.length > 0) setCompanyName(compRes.list[0].tradeName || '')
          setStats({
            vacantes: vacRes.total ?? vacRes.list.length,
            postulantes: appRes.total ?? appRes.list.length,
            colaboradores: linkRes.total ?? linkRes.list.length,
            evaluacionesPendientes: 0,
          })
        }
      } catch (e) {
        console.error('Error al cargar datos empresa:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [isAuthenticated, user])

  const statCards = [
    { label: 'Vacantes activas', value: stats.vacantes, icon: Briefcase, ...STAT_STYLES[0] },
    { label: 'Postulantes', value: stats.postulantes, icon: Users, ...STAT_STYLES[1] },
    { label: 'Colaboradores vinculados', value: stats.colaboradores, icon: UserCheck, ...STAT_STYLES[2] },
    { label: 'Evaluaciones pendientes', value: stats.evaluacionesPendientes, icon: Star, ...STAT_STYLES[3] },
  ]

  return (
    <EmpresaLayout
      title={`Bienvenido${companyName ? `, ${companyName}` : ''}`}
      subtitle="Aquí tienes un resumen de tu actividad en Beeznz Work"
    >
      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          >
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
              <Icon size={20} className={color} />
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">{loading ? '—' : value}</p>
            <p className="mt-0.5 text-sm text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Acciones rápidas */}
      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Acciones rápidas</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Publicar vacante', desc: 'Crea una nueva oportunidad laboral', to: '/empresa/vacantes', icon: Briefcase },
            { label: 'Ver postulantes', desc: 'Revisa candidatos a tus vacantes', to: '/empresa/postulantes', icon: UserCheck },
            { label: 'Evaluar colaboradores', desc: 'Registra evaluaciones mensuales', to: '/empresa/evaluaciones', icon: Star },
            { label: 'Completar perfil', desc: 'Mejora la visibilidad de tu empresa', to: '/empresa/perfil', icon: Users },
            { label: 'Ver reportes', desc: 'Métricas y desempeño del equipo', to: '/empresa/reportes', icon: TrendingUp },
            { label: 'Gestionar casos', desc: 'Reportes laborales pendientes', to: '/empresa/casos', icon: AlertTriangle },
          ].map(({ label, desc, to, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-amber-300 hover:shadow"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                <Icon size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 truncate">{desc}</p>
              </div>
              <ChevronRight size={15} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
            </Link>
          ))}
        </div>
      </div>

      {/* Onboarding */}
      {!loading && !companyName && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <FileText size={20} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Completa el perfil de tu empresa</p>
              <p className="mt-1 text-sm text-amber-700">
                Para publicar vacantes y atraer talento, primero debes completar el perfil de tu empresa.
              </p>
              <Link
                to="/empresa/perfil"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
              >
                Completar perfil <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaDashboard
